import { getFunctions, httpsCallable } from "firebase/functions";
import { storage } from "services/firebase";

/** Files larger than this are staged in Storage to avoid the 8MB inline callable limit. */
const MAX_INLINE_BYTES = 8 * 1024 * 1024;

const functions = getFunctions();

const uploadDocsCallable = httpsCallable(functions, "embeddeddocuments_uploaddocs", {
  timeout: 300000,
});
const listDocsCallable = httpsCallable(functions, "embeddeddocuments_listdocs", {
  timeout: 60000,
});
const listAvailableCallable = httpsCallable(functions, "embeddeddocuments_listavailable", {
  timeout: 60000,
});
const deleteDocsCallable = httpsCallable(functions, "embeddeddocuments_deletedocs", {
  timeout: 60000,
});
const setPromptAttachmentCallable = httpsCallable(
  functions,
  "embeddeddocuments_setpromptattachment",
  {
    timeout: 60000,
  }
);
const deleteByOrgCallable = httpsCallable(functions, "embeddeddocuments_deletebyorg", {
  timeout: 120000,
});
const deleteAllByOrgCallable = httpsCallable(
  functions,
  "embeddeddocuments_deleteallbyorg",
  {
    timeout: 60000,
  }
);
const getDeleteAllJobCallable = httpsCallable(
  functions,
  "embeddeddocuments_getdeletealljob",
  {
    timeout: 60000,
  }
);
const setPublicCallable = httpsCallable(functions, "embeddeddocuments_setpublic", {
  timeout: 60000,
});
const listLibraryCallable = httpsCallable(functions, "embeddeddocuments_listlibrary", {
  timeout: 60000,
});
const attachToPromptCallable = httpsCallable(
  functions,
  "embeddeddocuments_attachtoprompt",
  { timeout: 60000 },
);

function normalizePromptId(promptId) {
  return String(promptId || "").trim().toLowerCase();
}

/**
 * Convert a File object to a base64 string (data URI prefix stripped).
 * @param {(ratio: number) => void} [onReadProgress] — ratio in [0, 1] while loading; 1 when read completes.
 */
function fileToBase64(file, onReadProgress) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onprogress = (event) => {
      if (!onReadProgress) return;
      if (event.lengthComputable && event.total > 0) {
        onReadProgress(event.loaded / event.total);
      }
    };
    reader.onloadend = () => {
      if (reader.error) {
        reject(reader.error);
        return;
      }
      onReadProgress?.(1);
      const base64 = reader.result.split(",")[1];
      resolve(base64);
    };
    reader.onerror = () => reject(reader.error || new Error("Failed to read file."));
    reader.readAsDataURL(file);
  });
}

/**
 * Upload a single document file (PDF, DOCX, TXT, MD, or ZIP).
 * Files ≤ 8 MB are sent inline (base64). Larger files are staged in Firebase
 * Storage and the callable receives a storagePath instead — matching the
 * `storagePath` branch in embeddeddocuments_uploaddocs.
 * @param {File} file
 * @param {Object} metadata - arbitrary metadata to attach
 * @param {{ onReadProgress?: (ratio: number) => void, onUploading?: () => void }} [progress]
 * @returns {Promise<Object>} result from the function
 */
export async function uploadDocument(
  file,
  promptId,
  metadata = {},
  progress = {},
  scope = "global"
) {
  const { onReadProgress, onUploading } = progress;
  const normalizedPromptId = normalizePromptId(promptId);
  if (!normalizedPromptId) {
    throw new Error("A prompt ID is required to upload embedded documents.");
  }

  const fileType = file.type || "application/octet-stream";

  if (file.size > MAX_INLINE_BYTES) {
    const tmpPath = `embedded-docs-tmp/${normalizedPromptId}/${Date.now()}-${file.name}`;
    const ref = storage.ref(tmpPath);
    await ref.put(file);
    try {
      const result = await uploadDocsCallable({
        storagePath: tmpPath,
        fileName: file.name,
        fileType,
        promptId: normalizedPromptId,
        metadata,
        scope,
      });
      return result.data;
    } finally {
      ref.delete().catch(() => {});
    }
  }

  const fileBase64 = await fileToBase64(file);
  const result = await uploadDocsCallable({
    fileBase64,
    fileName: file.name,
    fileType,
    promptId: normalizedPromptId,
    metadata,
    scope,
  });
  return result.data;
}

/**
 * Ingest an existing Storage file as an embedded document, without routing the
 * bytes through the browser. The callable downloads the file server-side from
 * `storagePath` (e.g. a clinical reference's permanent file), extracts, chunks,
 * and embeds it. The source file is left in place — unlike uploadDocument's tmp
 * staging path, it is NOT deleted afterwards.
 * @param {{ storagePath: string, fileName: string, fileType?: string }} source
 * @param {string} promptId
 * @param {Object} metadata
 * @returns {Promise<Object>} result from the function
 */
export async function importFromStoragePath(
  source,
  promptId,
  metadata = {},
  scope = "global"
) {
  const normalizedPromptId = normalizePromptId(promptId);
  if (!normalizedPromptId) {
    throw new Error("A prompt ID is required to import embedded documents.");
  }
  if (!source?.storagePath) {
    throw new Error("A storage path is required to import embedded documents.");
  }
  const result = await uploadDocsCallable({
    storagePath: source.storagePath,
    fileName: source.fileName || "document",
    fileType: source.fileType || "application/octet-stream",
    promptId: normalizedPromptId,
    metadata,
    scope,
  });
  return result.data;
}

/**
 * List all embedded documents for the current user.
 * @returns {Promise<Array>} array of document metadata objects
 */
/**
 * @param {string} [scope] - "global", "org", or omitted for all visible to caller.
 */
export async function listDocuments(promptId, scope) {
  const normalizedPromptId = normalizePromptId(promptId);
  if (!normalizedPromptId) {
    return [];
  }
  const payload = { promptId: normalizedPromptId };
  if (scope) payload.scope = scope;
  const result = await listDocsCallable(payload);
  return result.data?.documents ?? [];
}

/**
 * List all embedded documents uploaded by the caller, annotated for this prompt.
 * @returns {Promise<Array>} array of document metadata objects
 */
export async function listAvailableDocuments(promptId) {
  const normalizedPromptId = normalizePromptId(promptId);
  if (!normalizedPromptId) {
    return [];
  }
  const result = await listAvailableCallable({ promptId: normalizedPromptId });
  return result.data?.documents ?? [];
}

/**
 * Delete one or more embedded documents.
 * - detachOnly:true removes the docs from the prompt only; they stay in the library
 *   even when this was their last prompt.
 * - everywhere:true permanently deletes the doc, its chunks, and its Storage file;
 *   promptId is not required (works on orphaned docs too).
 * - Default: detaches when shared with other prompts, deletes otherwise.
 * @param {string|string[]} docIds
 * @param {string|null} promptId
 * @param {{ everywhere?: boolean, detachOnly?: boolean }} [options]
 * @returns {Promise<Object>} result from the function
 */
export async function deleteDocument(docIds, promptId, options = {}) {
  const normalizedPromptId = normalizePromptId(promptId);
  const everywhere = options.everywhere === true;
  if (!normalizedPromptId && !everywhere) {
    throw new Error("A prompt ID is required to delete embedded documents.");
  }
  const ids = Array.isArray(docIds) ? docIds : [docIds];
  const result = await deleteDocsCallable({
    docIds: ids,
    promptId: normalizedPromptId || null,
    everywhere,
    detachOnly: options.detachOnly === true,
  });
  return result.data;
}

/**
 * Attach/detach an existing embedded document from one prompt.
 * @param {string} docId
 * @param {string} promptId
 * @param {boolean} attach
 * @returns {Promise<Object>} result from the function
 */
export async function updateDocumentPromptAttachment(docId, promptId, attach) {
  const normalizedPromptId = normalizePromptId(promptId);
  const normalizedDocId = String(docId || "").trim();
  if (!normalizedDocId || !normalizedPromptId) {
    throw new Error("A document ID and prompt ID are required.");
  }

  const result = await setPromptAttachmentCallable({
    docId: normalizedDocId,
    promptId: normalizedPromptId,
    attach: attach === true,
  });
  return result.data;
}

/**
 * Permanently remove one organization storage-usage document.
 * Deletes the original embedded file, embedding chunks, and matching clinical
 * reference documents for the same uploaded file.
 * @param {string} docId
 * @returns {Promise<Object>} result from the function
 */
export async function deleteDocumentByOrganization(docId) {
  const normalizedDocId = String(docId || "").trim();
  if (!normalizedDocId) {
    throw new Error("A document ID is required.");
  }

  const result = await deleteByOrgCallable({ docId: normalizedDocId });
  return result.data;
}

/**
 * Permanently remove all organization documents managed by storage usage.
 * @returns {Promise<Object>} cleanup result from the function
 */
export async function deleteAllDocumentsByOrganization() {
  const result = await deleteAllByOrgCallable({});
  return result.data;
}

/**
 * Fetch progress for a bulk organization document delete job.
 * @param {string} jobId
 * @returns {Promise<Object>} delete job status
 */
export async function getDeleteAllDocumentsJob(jobId) {
  const normalizedJobId = String(jobId || "").trim();
  if (!normalizedJobId) {
    throw new Error("A delete job ID is required.");
  }

  const result = await getDeleteAllJobCallable({ jobId: normalizedJobId });
  return result.data?.job ?? null;
}

/**
 * List all embedded documents visible to the caller (no promptId filter), so callers can
 * build a "library" picker. Each doc includes promptIds[] so the UI can show how many other
 * prompts already use it.
 * @param {{ scope?: "global" | "org" }} [options]
 * @returns {Promise<Array>}
 */
export async function listLibrary({ scope } = {}) {
  const payload = scope ? { scope } : {};
  const result = await listLibraryCallable(payload);
  return result.data?.documents ?? [];
}

/**
 * List storage usage rows plus total document count for destructive bulk actions.
 * @param {number} [limit=20]
 * @returns {Promise<{documents: Array, totalCount: number}>}
 */
export async function listDocumentUsageByOrganization(limit = 20) {
  const result = await listByOrgCallable({ limit });
  const data = result.data || {};
  return {
    documents: data.documents ?? [],
    totalCount: Number(data.totalCount ?? data.count ?? 0) || 0,
  };
}

/**
 * Attach existing documents (by ID) to a promptId. The docs' promptIds[] array gains
 * `promptId`; no re-upload, no re-embed.
 * @param {string[]} docIds
 * @param {string} promptId
 */
export async function attachToPrompt(docIds, promptId) {
  const normalizedPromptId = normalizePromptId(promptId);
  if (!normalizedPromptId) {
    throw new Error("A prompt ID is required to attach embedded documents.");
  }
  const ids = (Array.isArray(docIds) ? docIds : [docIds])
    .map((id) => String(id).trim())
    .filter(Boolean);
  if (!ids.length) {
    return { success: true, attached: [], errors: [] };
  }
  const result = await attachToPromptCallable({
    docIds: ids,
    promptId: normalizedPromptId,
  });
  return result.data;
}

/**
 * Update the public flag for one embedded document.
 * @param {string} docId
 * @param {boolean} isPublic
 * @returns {Promise<Object>} result from the function
 */
export async function updateDocumentPublic(docId, isPublic, promptId) {
  const normalizedPromptId = normalizePromptId(promptId);
  if (!normalizedPromptId) {
    throw new Error("A prompt ID is required to update embedded document visibility.");
  }
  const result = await setPublicCallable({ docId, isPublic, promptId: normalizedPromptId });
  return result.data;
}
